<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 flex items-center justify-center z-50"
      @click.self="$emit('close')"
    >
      <div
        class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
      ></div>
      <div
        class="bg-gray-800 text-white rounded-lg shadow-xl border border-gray-700 p-5 max-w-md z-10 w-full mx-4 md:w-auto"
      >
        <div class="flex items-start mb-4">
          <div
            class="flex-shrink-0 mr-4 p-2 rounded-full"
            :class="[
              type === 'danger'
                ? 'bg-red-900 text-red-400'
                : type === 'warning'
                ? 'bg-yellow-900 text-yellow-400'
                : 'bg-blue-900 text-blue-400',
            ]"
          >
            <!-- Icon based on type -->
            <svg
              v-if="type === 'danger'"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <svg
              v-else-if="type === 'warning'"
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>

          <div class="flex-1">
            <h3 class="text-lg font-medium" :class="titleClass">{{ title }}</h3>
            <p class="mt-2 text-gray-300">{{ message }}</p>
          </div>
        </div>

        <div class="flex justify-end space-x-3 mt-6">
          <button
            @click="$emit('close')"
            class="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600 transition-colors font-medium"
          >
            {{ cancelText }}
          </button>
          <button
            @click="$emit('confirm')"
            class="px-4 py-2 rounded-md font-medium transition-colors"
            :class="confirmButtonClass"
            :disabled="isLoading"
          >
            <svg
              v-if="isLoading"
              class="animate-spin h-4 w-4 mr-2 inline-block"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';

export default defineComponent({
  name: 'ConfirmationModal',
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'info',
      validator: (value: string) => {
        return ['info', 'warning', 'danger', 'success'].includes(value);
      },
    },
    confirmText: {
      type: String,
      default: 'Potvrdi',
    },
    cancelText: {
      type: String,
      default: 'Otkaži',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm'],
  setup(props) {
    const titleClass = computed(() => {
      switch (props.type) {
        case 'danger':
          return 'text-red-400';
        case 'warning':
          return 'text-yellow-400';
        case 'success':
          return 'text-green-400';
        default:
          return 'text-blue-400';
      }
    });

    const confirmButtonClass = computed(() => {
      switch (props.type) {
        case 'danger':
          return 'bg-red-600 hover:bg-red-700 text-white';
        case 'warning':
          return 'bg-yellow-600 hover:bg-yellow-700 text-white';
        case 'success':
          return 'bg-green-600 hover:bg-green-700 text-white';
        default:
          return 'bg-blue-600 hover:bg-blue-700 text-white';
      }
    });

    return {
      titleClass,
      confirmButtonClass,
    };
  },
});
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
